<template>
  <v-dialog :value="value" max-width="900" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title>
        Select Media
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 500px;">
        <v-row dense class="mb-3">
          <v-col cols="6">
            <v-text-field
              v-model="search"
              label="Search"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
            />
          </v-col>
        </v-row>

        <div
          class="media-picker-dropzone mb-3"
          @dragover.prevent
          @drop.prevent="onDrop"
          @click="$refs.fileInput && $refs.fileInput.click()"
        >
          <v-icon color="grey">mdi-cloud-upload</v-icon>
          <span class="grey--text ml-2">Drop file or click to upload</span>
        </div>
        <input
          ref="fileInput"
          type="file"
          style="display: none;"
          :accept="mimeFilter && mimeFilter.startsWith('image/') ? 'image/*' : '*'"
          @change="onFileChange"
        />
        <v-progress-linear v-if="uploading" :value="uploadProgress" height="4" class="mb-3" />

        <v-row v-if="items.length" dense>
          <v-col
            v-for="file in items"
            :key="file.media_file_id"
            cols="2"
          >
            <v-card
              hover
              outlined
              :class="{ 'media-picker-selected': selected && selected.media_file_id === file.media_file_id }"
              @click="selected = file"
            >
              <v-img
                v-if="file.mime_type && file.mime_type.startsWith('image/')"
                :src="file.url"
                height="80"
                contain
              />
              <div v-else class="media-picker-file-placeholder">
                <v-icon size="32" color="grey">mdi-file-document</v-icon>
              </div>
              <v-card-text class="pa-1 text-caption text-truncate">
                {{ file.filename }}
                <v-chip v-if="!file.entity_id || file.entity_id === 0" x-small color="blue" outlined class="ml-1">
                  <v-icon x-small left>mdi-earth</v-icon> Global
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <div v-else-if="!loading" class="grey--text text-center py-4">No files found</div>
        <v-progress-linear v-if="loading" indeterminate class="mt-2" />
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn color="primary" :disabled="!selected" @click="confirmSelect">
          Select
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { API } from '@/utils/api'

export default {
  name: 'MediaPickerDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: [Number, String],
      default: null,
    },
    mimeFilter: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      selected: null,
      search: '',
      uploading: false,
      uploadProgress: 0,
    }
  },
  watch: {
    value(open) {
      if (open) {
        this.selected = null
        this.search = ''
        this.loadItems()
      }
    },
    search() {
      if (this.value) {
        this.loadItems()
      }
    },
    mimeFilter() {
      if (this.value) {
        this.loadItems()
      }
    },
  },
  methods: {
    close() {
      this.$emit('input', false)
    },
    confirmSelect() {
      if (!this.selected) {
        return
      }
      this.$emit('select', this.selected)
      this.close()
    },
    loadItems() {
      if (!this.entityId) {
        this.items = []
        return
      }
      this.loading = true
      const params = {
        entity_id: this.entityId,
        perpage: 100,
      }
      if (this.mimeFilter) {
        params.mime_type = this.mimeFilter
      }
      API.call('/cms/media-file/list.json', params)
        .then(r => {
          let items = r.data.items || []
          if (this.search) {
            const q = this.search.toLowerCase()
            items = items.filter(f => (f.filename || '').toLowerCase().includes(q))
          }
          this.items = items
        })
        .finally(() => {
          this.loading = false
        })
    },
    async onDrop(e) {
      const files = Array.from(e.dataTransfer.files)
      if (files.length) {
        await this.uploadFile(files[0])
      }
    },
    async onFileChange(e) {
      const files = Array.from(e.target.files)
      if (files.length) {
        await this.uploadFile(files[0])
      }
      e.target.value = ''
    },
    async uploadFile(file) {
      if (!this.entityId) {
        return
      }
      this.uploading = true
      this.uploadProgress = 0
      try {
        this.uploadProgress = 20
        const reader = new FileReader()
        const buffer = await new Promise((resolve, reject) => {
          reader.onload = () => resolve(reader.result)
          reader.onerror = () => reject(new Error('Failed to read file'))
          reader.readAsArrayBuffer(file)
        })

        this.uploadProgress = 50
        const r = await API.call('/cms/media-file/upload.json', {
          entity_id: this.entityId,
          filename: file.name,
          mime_type: file.type,
          file: Array.from(new Uint8Array(buffer)),
        })

        this.uploadProgress = 100
        const item = (r && r.data && r.data.item) ? r.data.item : (r && r.data)
        if (item && item.media_file_id != null) {
          this.$emit('select', item)
          this.close()
        } else {
          this.loadItems()
        }
      } catch (e) {
        // silent
      } finally {
        this.uploading = false
      }
    },
  },
}
</script>

<style scoped>
.media-picker-dropzone {
  border: 2px dashed #ccc !important;
  border-radius: 6px !important;
  padding: 16px !important;
  text-align: center !important;
  cursor: pointer !important;
}
.media-picker-dropzone:hover {
  border-color: #1976d2 !important;
  background: #f5f9ff !important;
}
.media-picker-selected {
  outline: 2px solid #1976d2 !important;
}
.media-picker-file-placeholder {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}
</style>
